import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useStartWalletOtpMutation,
  useResendWalletOtpMutation,
  useVerifyWalletOtpMutation,
} from '../services/customerWalletOtpApi';
import { useToast } from '../components/common/ToastProvider';

const RESEND_COOLDOWN_SECONDS = 30;

// Drives the "sending → pending → verified" state machine for one wallet
// type (store_credit | loyalty_points) against one order. Used independently
// by each of the three real UI surfaces that can debit a customer's wallet —
// the CheckoutPage Store Credit tile, CheckoutLoyaltyPanel's Apply flow, and
// ExchangeCheckoutModal's customer_pays payment picker — so the state
// machine lives here once instead of being re-implemented three times.
export function useWalletOtpVerification({ orderId, walletType }) {
  const showToast = useToast();
  const [status, setStatus] = useState('idle'); // idle | sending | pending | verified | invalidated | blocked_phone | blocked_connector
  const [verification, setVerification] = useState(null); // { id, expires_at }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const countdownRef = useRef(null);
  const cooldownRef = useRef(null);

  const [startMutation, { isLoading: isStarting }] = useStartWalletOtpMutation();
  const [resendMutation, { isLoading: isResending }] = useResendWalletOtpMutation();
  const [verifyMutation, { isLoading: isVerifying }] = useVerifyWalletOtpMutation();

  useEffect(() => () => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
  }, []);

  const startCountdown = (expiresAt) => {
    clearInterval(countdownRef.current);
    const compute = () => Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
    setSecondsLeft(compute());
    countdownRef.current = setInterval(() => setSecondsLeft(compute()), 1000);
  };

  const startCooldown = () => {
    clearInterval(cooldownRef.current);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const start = useCallback(async () => {
    setStatus('sending');
    try {
      const res = await startMutation({ orderId, walletType }).unwrap();
      const v = res?.data?.customer_wallet_otp_verification;
      setVerification(v);
      setStatus('pending');
      startCountdown(v?.expires_at);
      startCooldown();
      setModalOpen(true);
      showToast('Code texted to customer’s phone on file');
      return v;
    } catch (err) {
      const message = err?.data?.error || '';
      if (message.toLowerCase().includes('no phone number')) setStatus('blocked_phone');
      else if (message.toLowerCase().includes('not available')) setStatus('blocked_connector');
      else {
        setStatus('idle');
        showToast(message || 'Could not start verification', 'error');
      }
      return null;
    }
  }, [orderId, walletType, startMutation]);

  const resend = useCallback(async () => {
    if (!verification) return;
    try {
      const res = await resendMutation({ orderId, verificationId: verification.id }).unwrap();
      const v = res?.data?.customer_wallet_otp_verification || verification;
      setVerification(v);
      startCountdown(v.expires_at);
      startCooldown();
      showToast('New code sent to the customer');
    } catch (err) {
      showToast(err?.data?.error || 'Could not resend the code', 'error');
    }
  }, [orderId, verification, resendMutation]);

  const verify = useCallback(async (code) => {
    if (!verification) return { ok: false, error: 'No verification in progress' };
    try {
      const res = await verifyMutation({ orderId, verificationId: verification.id, code }).unwrap();
      clearInterval(countdownRef.current);
      clearInterval(cooldownRef.current);
      setStatus('verified');
      setModalOpen(false);
      return { ok: true, data: res?.data };
    } catch (err) {
      return { ok: false, error: err?.data?.error || 'Incorrect verification code' };
    }
  }, [orderId, verification, verifyMutation]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  // Order changed after verification — consent for the old total no longer applies.
  const invalidate = useCallback(() => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
    setStatus('invalidated');
    setVerification(null);
    setModalOpen(false);
  }, []);

  const reset = useCallback(() => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
    setStatus('idle');
    setVerification(null);
    setModalOpen(false);
  }, []);

  // Combined mode: the other wallet type is already verified for this order,
  // so this one is satisfied instantly — no second SMS, no modal.
  const markVerifiedInstantly = useCallback(() => setStatus('verified'), []);

  return {
    status, verification, secondsLeft, modalOpen, resendCooldown,
    isStarting, isResending, isVerifying,
    start, resend, verify, openModal, closeModal, invalidate, reset, markVerifiedInstantly,
  };
}
